import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Pagination from '@features/search/Pagination';
import ProductGrid from '@features/search/ProductGrid';
import Sidebar from '@widgets/Sidebar';
import Breadcrumbs, { BreadcrumbItem } from '../shared/components/Breadcrumbs';
import { Category } from '@/types/category';
import { sortCategoriesAlphanumeric } from '@shared/lib';

const Catalog: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { name: 'Catalog', path: '/catalog' }
  ]);

  // Прокрутка страницы вверх при монтировании компонента или изменении категории
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  const API_URL = "https://huter.diy/wordpress/wp-json/wc/v3/products/categories";
  const API_KEY = 'ck_b633c1877cf1de3bdbb01117e1fa8215b9b99e43';
  const API_SECRET = 'cs_5c69080f905a13fda5aefffb41f6ac3988ab6478';

  // Загрузка категорий
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}?consumer_key=${API_KEY}&consumer_secret=${API_SECRET}`);
        const data: Category[] = await response.json();
        // Применяем алфавитно-цифровую сортировку к категориям
        const sortedCategories = sortCategoriesAlphanumeric(data);
        setCategories(sortedCategories);

        // Если есть slug категории в URL, находим соответствующую категорию
        if (categorySlug) {
          const foundCategory = data.find((cat) => cat.slug === categorySlug);
          if (foundCategory) {
            setSelectedCategory(foundCategory.id);
            updateBreadcrumbs(foundCategory);
          }
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [categorySlug]);

  // Проверяем параметр page в URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (!isNaN(pageNumber)) {
        setCurrentPage(pageNumber);
      }
    }
  }, [location]);

  useEffect(() => {
    localStorage.setItem('catalogPage', currentPage.toString());
  }, [currentPage]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
    // Обновляем URL с новым параметром page
    navigate(`/catalog${categorySlug ? `/${categorySlug}` : ''}?page=${pageNumber}`);
  };

  // Функция для обновления хлебных крошек при выборе категории
  const updateBreadcrumbs = (category: Category) => {
    const newBreadcrumbs: BreadcrumbItem[] = [{ name: 'Catalog', path: '/catalog' }];
    
    if (category) {
      if (category.parent === 0) {
        // Если это основная категория
        newBreadcrumbs.push({
          name: category.name,
          path: `/catalog/${category.slug}`
        });
      } else {
        // Если это подкатегория, найдем родительскую категорию
        const parentCategory = categories.find(cat => cat.id === category.parent);
        if (parentCategory) {
          newBreadcrumbs.push({
            name: parentCategory.name,
            path: `/catalog/${parentCategory.slug}`
          });
        }
        newBreadcrumbs.push({
          name: category.name,
          path: `/catalog/${category.slug}`
        });
      }
    }
    
    setBreadcrumbs(newBreadcrumbs);
  };

  // Обновляем обработчик выбора категории
  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
    const selectedCat = categories.find(cat => cat.id === categoryId);
    if (selectedCat) {
      updateBreadcrumbs(selectedCat);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumbs items={breadcrumbs} />
      <div className="flex flex-col md:flex-row gap-8">
        <Sidebar
          categories={categories}
          onCategorySelect={handleCategorySelect}
          selectedCategory={selectedCategory}
        />
        <div className="flex-1">
          <ProductGrid
            currentPage={currentPage}
            categoryId={selectedCategory}
            onTotalItemsChange={setTotalItems}
          />
          {totalItems > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalog;